import {
  faChevronRight,
  faCommentDots,
  faDollarSign,
  faFaceGrin,
  faGear,
  faGlobe,
  faPenLine,
  faStethoscope,
  IconDefinition,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { JoonUIColor, Typography } from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"

import useSettingsAccountModalStore from "./hooks/useSettingsAccountModalStore"
import useSettingsLanguageModalStore from "./hooks/useSettingsLanguageModalStore"
import useSettingsSubscriptionModalStore from "./hooks/useSettingsSubscriptionModalStore"
import SettingsAccountModal from "./SettingsAccountModal"
import SettingsLanguageModal from "./SettingsLanguageModal"
import SettingsSubscriptionModal from "./SettingsSubscriptionModal"
import TextMessageSettings from "./TextMessageSettings"
import useTextMessageSettingsStore from "./useTextMessageSettingsModalStore"
import ChildAuthCode from "../../components/childAuthCode/ChildAuthCode"
import PageContentWrapper from "../../components/pageContentWrapper/PageContentWrapper"
import PageHeader from "../../components/pageHeader/PageHeader"
import PageWrapper from "../../components/pageWrapper/PageWrapper"
import LogoutButton from "../../components/sidebar/LogoutButton"
import { GRADIENTS } from "../../constants"
import useDocumentTitle from "../../hooks/useDocumentTitle"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { requireAuth } from "../../util/auth"
import { useLinkTherapistModalStore } from "../care/therapists/useLinkTherapistModalStore"

function SettingsPage() {
  useDocumentTitle("Joon - Settings")
  const { t } = useTranslation()

  return (
    <PageWrapper style={{ background: GRADIENTS.gradient100 }}>
      <PageHeader title={t("NAV_SETTINGS")} />
      <ChildAuthCode />
      <PageContentWrapper mobileGutter={true}>
        <SettingsPageContent />
      </PageContentWrapper>
    </PageWrapper>
  )
}

const SettingsNavItem = ({
  icon,
  text,
  onClick,
  isLastItem = false,
}: {
  icon: IconDefinition
  text: string
  onClick: () => void
  isLastItem: boolean
}) => {
  return (
    <button onClick={onClick}>
      <div
        className={`flex items-center gap-3 p-3 pr-4 w-full bg-white hover:bg-bgXLightGray ${
          isLastItem ? "" : "border-b border-borderPrimary"
        }`}
      >
        <FontAwesomeIcon
          icon={icon}
          fontSize={16}
          className="w-4"
          color={JoonUIColor.icon.accent}
        />
        <Typography variant="body" textAlign="left">
          {text}
        </Typography>
        <FontAwesomeIcon
          icon={faChevronRight}
          fontSize={20}
          color={JoonUIColor.icon.neutral}
          className="ml-auto"
        />
      </div>
    </button>
  )
}

export const SettingsPageContent = () => {
  const { t } = useTranslation()
  const { onOpen: openTextMessageSettings } = useTextMessageSettingsStore()
  const { onOpen: openLinkTherapistModal } = useLinkTherapistModalStore()
  const { onOpen: openSettingsAccountModal } = useSettingsAccountModalStore()
  const { onOpen: openSettingsLanguageModal } = useSettingsLanguageModalStore()
  const { onOpen: openSettingsSubscriptionModal } =
    useSettingsSubscriptionModalStore()

  const onClickParentResourcesGroup = () => {
    window.open("https://www.facebook.com/groups/joonadhdcommunity/", "_blank")
  }

  const onClickContactUs = () => {
    window.open(
      "https://docs.google.com/forms/d/e/1FAIpQLScol6cyUpFy6QMOCTQsHe5dnchnfAlobyeMF5arwYnnYmmdRg/viewform",
      "_blank"
    )
  }

  const onClickTextMessageSettings = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_TEXT_MESSAGE_SETTINGS)
    openTextMessageSettings()
  }

  const onClickLanguage = () => {
    openSettingsLanguageModal()
  }

  const onClickLinkTherapist = () => {
    openLinkTherapistModal(false)
  }

  const onClickAccount = () => {
    openSettingsAccountModal()
  }

  const onClickSubscription = () => {
    openSettingsSubscriptionModal()
  }

  return (
    <>
      <div className="flex flex-col rounded-lg overflow-hidden border border-borderPrimary mb-6">
        <SettingsNavItem
          icon={faGear}
          text={t("SETTINGS_MANAGE_ACCOUNT")}
          onClick={onClickAccount}
          isLastItem={false}
        />
        <SettingsNavItem
          icon={faDollarSign}
          text={t("SETTINGS_MANAGE_SUBSCRIPTION")}
          onClick={onClickSubscription}
          isLastItem={false}
        />
        <SettingsNavItem
          icon={faCommentDots}
          text={t("SETTINGS_TEXT_MESSAGE")}
          onClick={onClickTextMessageSettings}
          isLastItem={false}
        />
        <SettingsNavItem
          icon={faGlobe}
          text={t("SETTINGS_LANGUAGE")}
          onClick={onClickLanguage}
          isLastItem={false}
        />
        <SettingsNavItem
          icon={faStethoscope}
          text={t("SETTINGS_LINK_THERAPIST")}
          onClick={onClickLinkTherapist}
          isLastItem={false}
        />
        <SettingsNavItem
          icon={faFaceGrin}
          text={t("SETTINGS_COMMUNITY")}
          onClick={onClickParentResourcesGroup}
          isLastItem={false}
        />
        <SettingsNavItem
          icon={faPenLine}
          text={t("SETTINGS_CONTACT_US")}
          onClick={onClickContactUs}
          isLastItem={true}
        />
      </div>
      <LogoutButton />
      <TextMessageSettings />
      <SettingsAccountModal />
      <SettingsLanguageModal />
      <SettingsSubscriptionModal />
    </>
  )
}

const SettingsPageWithAuth = requireAuth(SettingsPage)
export default SettingsPageWithAuth
